// DEHORS du composant principal

import Settings from '@mui/icons-material/Tune';
import {
  Autocomplete,
  AutocompleteChangeReason,
  Badge,
  Box,
  TextField,
  Tooltip,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

type FilterComponentProps = {
  importedParcelFilterValue: string | null;
  importedParcelFilterOptions: string[];
  setImportedParcelFilterValue: (value: string | null) => void;
  setAreAssociatedImportedParcelsHiden: (value: boolean | ((prev: boolean) => boolean)) => void;
  areAssociatedImportedParcelsHiden: boolean;
  filteredImportedParcelsCount: number;
};

const FilterComponent = memo<FilterComponentProps>(
  ({
    importedParcelFilterValue,
    importedParcelFilterOptions,
    setImportedParcelFilterValue,
    setAreAssociatedImportedParcelsHiden,
    areAssociatedImportedParcelsHiden,
    filteredImportedParcelsCount,
  }) => {
    const { t } = useTranslation();
    const [toggleFilter, setToggleFilter] = useState<boolean>(false);

    const handleFilterChange = useCallback(
      (
        event: React.SyntheticEvent<Element, Event>,
        value: string | null,
        reason: AutocompleteChangeReason,
      ) => {
        if (reason === 'clear') {
          setImportedParcelFilterValue(null);
          return;
        } else {
          if (value) {
            const cleanedValue = value.trim().toLowerCase();
            cleanedValue?.length > 0
              ? setImportedParcelFilterValue(cleanedValue)
              : setImportedParcelFilterValue(null);
          }
        }
      },
      [setImportedParcelFilterValue],
    );

    const handleToggleFilter = useCallback(() => {
      setToggleFilter((prev) => !prev);
    }, []);

    const handleToggleAssociatedParcels = useCallback(() => {
      setAreAssociatedImportedParcelsHiden((prev: boolean) => !prev);
    }, [setAreAssociatedImportedParcelsHiden]);

    // Memoization des styles dynamiques
    const filterContainerStyle = useMemo(
      () => ({
        width: toggleFilter ? 'calc(100% - 24px)' : '160px',
        height: toggleFilter ? '35px' : '24px',
      }),
      [toggleFilter],
    );

    const filterTextStyle = useMemo(
      () => ({
        width: !toggleFilter ? '137px' : '0px',
        gap: !toggleFilter ? '5px' : '0px',
      }),
      [toggleFilter],
    );

    const filterContentStyle = useMemo(
      () => ({
        width: toggleFilter ? '100%' : '0%',
        height: toggleFilter ? '50px' : '0px',
      }),
      [toggleFilter],
    );

    // Memoization du tooltip content
    const tooltipContent = useMemo(
      () => (
        <Box className="custom_tooltip">
          <span>
            {t(
              areAssociatedImportedParcelsHiden
                ? 'procedureImport.showAssociatedParcels'
                : 'procedureImport.hideAssociatedParcels',
            )}
          </span>
        </Box>
      ),
      [areAssociatedImportedParcelsHiden, t],
    );

    const hideIconClassName = useMemo(
      () =>
        `hide_associed_parcels_icon ${
          areAssociatedImportedParcelsHiden ? 'parcels_hidden' : 'parcels_visible'
        }`,
      [areAssociatedImportedParcelsHiden],
    );

    return (
      <Box className="filter" sx={filterContainerStyle}>
        <Box className="filter_toggle" onClick={handleToggleFilter}>
          <div className="filter_text" style={filterTextStyle}>
            {t('common.showFilters')}
          </div>
          <Settings className="filter_icon" />
        </Box>

        <Box className="filter_content" sx={filterContentStyle}>
          <Autocomplete
            className="filter_autocomplete"
            size="small"
            freeSolo
            autoComplete
            forcePopupIcon={false}
            options={importedParcelFilterOptions}
            value={importedParcelFilterValue}
            onChange={handleFilterChange}
            renderInput={(params) => (
              <TextField {...params} label={t('procedureImport.filterByParcelName')} />
            )}
          />

          <Tooltip
            arrow
            slotProps={{
              popper: {
                sx: { inset: '5px auto auto -2px !important' },
              },
            }}
            title={tooltipContent}
          >
            <Badge
              badgeContent={filteredImportedParcelsCount}
              color="primary"
              invisible={!areAssociatedImportedParcelsHiden}
            >
              <VisibilityOffIcon
                onClick={handleToggleAssociatedParcels}
                className={hideIconClassName}
              />
            </Badge>
          </Tooltip>
        </Box>
      </Box>
    );
  },
);

FilterComponent.displayName = 'ImportFilterComponent';

export default FilterComponent;
